import { useEffect } from 'react'
import { useAuth } from '../context/AuthContext'
import { isStandalone } from '../utils/standalone'

// Bumped from 'bt_pwa_tracked' when the backend started recording which origin
// an install was reported from. Everyone who installed before that has the old
// key set and would never report again, leaving their origin permanently NULL —
// i.e. the entire pre-migration installed base, which is the population most
// worth seeing. The bump costs one extra request per install, once.
const PWA_TRACKED_KEY = 'bt_pwa_tracked_v2'

/**
 * Detects standalone (PWA) mode and reports it to the backend once.
 * Uses localStorage to avoid repeat API calls.
 * The request is fire-and-forget (non-blocking).
 *
 * localStorage is origin-scoped, which is load-bearing here rather than
 * incidental: it means a user who reinstalls on btvision.app reports again from
 * there — the old origin's key is invisible to the new one — so the recorded
 * origin follows them across the migration instead of being stuck at whichever
 * domain they first installed from.
 */
export function usePwaTracking() {
  const { isAuthenticated } = useAuth()

  useEffect(() => {
    if (!isAuthenticated) return

    if (!isStandalone()) return
    if (localStorage.getItem(PWA_TRACKED_KEY)) return

    const token = localStorage.getItem('sb-access-token')
    if (!token) return

    const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:3000'

    fetch(`${apiUrl}/api/auth/pwa-installed`, {
      method: 'POST',
      headers: { Authorization: `Bearer ${token}` },
    })
      .then((res) => {
        if (res.ok) localStorage.setItem(PWA_TRACKED_KEY, '1')
      })
      .catch(() => {
        // Non-critical — will retry on next load
      })
  }, [isAuthenticated])
}
